import React from "react";

import { faBars } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { SaleAction } from "actions";

import Header, { HeaderDropdown } from "components/Header";
import SaleyardSelect from "components/Header/SaleyardSelect";
import { Column, PaddedIcon, Row } from "components/Layout";
import OnlineStatus from "components/OnlineStatus";
import RolePicker from "components/RolePicker";
import { StaffActions } from "components/StaffActions";
import Subtitle from "components/Text/Subtitle";

import { LOAD_MORE_SALES } from "constants/sale";
import { Settings } from "constants/settings";
import { userTypes } from "constants/users";

import { ForHooksOrPaddock, ForLivestockAuction } from "containers/ForSaleType";
import { ForStaff } from "containers/ForUserType";

import { allFieldsEqual } from "lib/compare";

import {
  getCurrentSale,
  getDashboardFetchingStatus,
  getOnlineStatus,
  getSetting,
  selectIsUserOfType,
  selectSaleOptions,
  selectShowCheckPoints,
} from "selectors";

function RightColumnComponent() {
  const onlineState = useSelector(getOnlineStatus);
  const isFetchingDashboardData = useSelector(getDashboardFetchingStatus);

  return (
    <>
      <ForStaff>
        <StaffActions />
      </ForStaff>
      <RolePicker />
      <OnlineStatus
        status={onlineState.status}
        pendingChangesCount={onlineState.changesOutboxSize}
        isFetching={isFetchingDashboardData}
      />
    </>
  );
}

const SaleSubtitle = styled(Subtitle)(
  ({ theme }) => `
  margin: -${theme.space[2]}px 0 ${theme.space[1]}px ${theme.space[1]}px;
`,
);

const RightColumn = React.memo(RightColumnComponent);

function SaleyardHeader(props) {
  const {
    isMobile,
    onClickHamburgerMenu,
    showSaleSelector,
    saleyards,
    showSaleyardSelector,
    newSale,
    goToSale,
    saleId,
    changeSaleyard,
    saleyard,
  } = props;

  let saleOptions = useSelector(selectSaleOptions);
  const livestockSale = useSelector(getCurrentSale);
  const stickySale = useSelector(getSetting(Settings.stickySale));
  const saleyardOptions = saleyards.map(sy => ({
    id: sy.name,
    name: sy.name,
  }));
  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));

  const isSaleWatcher = useSelector(
    selectIsUserOfType([userTypes.SALE_WATCHER]),
  );

  const dispatch = useDispatch();

  if (
    !saleOptions.find(sale => sale.id === LOAD_MORE_SALES) &&
    !isSaleWatcher &&
    !allSalesLoaded
  ) {
    saleOptions.push({
      id: LOAD_MORE_SALES,
      name: LOAD_MORE_SALES,
    });
  }

  if (allSalesLoaded) {
    saleOptions = saleOptions.filter(obj => {
      return obj.id !== LOAD_MORE_SALES;
    });
  }

  const onChangeSale = saleId => {
    if (saleId) {
      goToSale(saleId);
    } else {
      dispatch(SaleAction.request({ fetchAll: true }));
    }
  };

  const showCheckPoints = useSelector(selectShowCheckPoints);

  return (
    <Header isMobile={isMobile}>
      {isMobile && (
        <PaddedIcon onClick={onClickHamburgerMenu}>
          <WrapperMenuButton data-tour="menubar" icon={faBars} />
        </PaddedIcon>
      )}
      <FullHeightRow justifyBetween flexGrow alignCenter>
        <IndentedHeaderRow flexGrow isMobile={isMobile}>
          {showSaleyardSelector && (
            <SaleyardSelect
              dataTour="saleSelector"
              options={saleyardOptions}
              onChange={changeSaleyard}
              selectedOption={saleyard}
              isMobile={isMobile}
              wideDropdown
            />
          )}
          {showSaleSelector && (
            <Column>
              <HeaderDropdown
                dataTour="saleSelector"
                onAddSale={newSale}
                onChange={onChangeSale}
                options={saleOptions}
                selectedOption={parseInt(saleId, 10)}
                isMobile={isMobile}
                wideDropdown
                showCheckPoints={showCheckPoints}
                placeholderText={
                  stickySale && livestockSale
                    ? undefined // Dont show placeholder text if sticky sale and livestock sale exist
                    : "Select A Sale To Work On"
                }
                allSalesLoaded={allSalesLoaded}
                loadSales={() =>
                  dispatch(SaleAction.request({ fetchAll: true }))
                }
              />
              {livestockSale && (
                <>
                  <ForLivestockAuction>
                    <SaleSubtitle data-tour="saleSubTitle">
                      {livestockSale.sale_code}: {livestockSale.sale_type} |{" "}
                      {livestockSale.saleyard_name}
                    </SaleSubtitle>
                  </ForLivestockAuction>

                  <ForHooksOrPaddock>
                    <SaleSubtitle>
                      {livestockSale.sale_code}: {livestockSale.sale_type}
                    </SaleSubtitle>
                  </ForHooksOrPaddock>
                </>
              )}
            </Column>
          )}
        </IndentedHeaderRow>
        <RightColumn />
      </FullHeightRow>
    </Header>
  );
}

export default React.memo(SaleyardHeader, (oldProps, newProps) =>
  allFieldsEqual(oldProps, newProps, [
    "goToSale",
    "isMobile",
    "showSaleSelector",
    "showSaleyardSelector",
    "saleyards",
    "saleId",
    "pendingChangesCount",
    "isFetchingDashboardData",
  ]),
);

const WrapperMenuButton = styled(FontAwesomeIcon)`
  font-size: 24px;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
`;

const FullHeightRow = styled(Row)`
  height: 100%;
`;

const IndentedHeaderRow = styled(Row)`
  margin: ${({ isMobile }) => `${isMobile ? "inherit" : "0 18px"}`};
`;
